import { Link, Outlet, createRootRoute, useNavigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { api } from '@/lib/api'
import { clearAuthState, getAuthState } from '@/lib/auth-store'
import statureBoostMark from '@/assets/brand/stature-logo.svg'
import styles from './__root.module.css'

export const Route = createRootRoute({
  component: RootComponent,
})

function getAccountInitials(
  name: string | null | undefined,
  email: string | null | undefined
): string {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 1) {
      return parts[0]!.slice(0, 2).toUpperCase()
    }
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase()
  }

  if (email && email.length > 0) {
    return email.slice(0, 2).toUpperCase()
  }

  return 'SB'
}

function RootComponent() {
  const navigate = useNavigate()
  const auth = getAuthState()
  const accountInitials = getAccountInitials(auth.user?.name, auth.user?.email)
  const accountName = auth.user?.name?.trim().length ? auth.user.name : 'StatureBoost account'
  const accountEmail = auth.user?.email ?? ''

  const handleLogout = async (): Promise<void> => {
    try {
      await api.logout()
    } catch {
      // Local logout should still proceed if network is unavailable.
    }

    clearAuthState()
    await navigate({ to: '/login' })
  }

  return (
    <div className={styles.pageShell}>
      <header className={styles.header}>
        <Link to="/" className={styles.brand}>
          <img src={statureBoostMark} alt="" className={styles.brandMark} />
          <span className={styles.brandText}>StatureBoost</span>
        </Link>

        <nav className={styles.nav}>
          {auth.isAuthenticated ? (
            <>
              <Link to="/home" className={styles.navLink}>
                Home
              </Link>
              <Link to="/workspace" className={styles.navLink}>
                Workspace
              </Link>
              <Link to="/insights" className={styles.navLink}>
                Insights
              </Link>
              <Link to="/audience" className={styles.navLink}>
                Audience
              </Link>
              <details className={styles.accountMenu}>
                <summary className={styles.accountTrigger} aria-label="Account menu">
                  <span className={styles.accountAvatar}>{accountInitials}</span>
                </summary>
                <div className={styles.accountDropdown}>
                  <p className={styles.accountName}>{accountName}</p>
                  <p className={styles.accountEmail}>{accountEmail}</p>
                  <div className={styles.accountDivider} />
                  <Link to="/settings" className={styles.accountItem}>
                    Account settings
                  </Link>
                  <button type="button" className={styles.accountLogout} onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </details>
            </>
          ) : (
            <>
              <Link to="/login" className={styles.navLink}>
                Login
              </Link>
              <Link to="/signup" className={styles.navCta}>
                Sign up
              </Link>
            </>
          )}
        </nav>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </div>
  )
}
